import type { ItemDetail, Version } from "@stratcms/sdk";
import { api, can, explain, isApprover, session } from "./api";
import { fmtRelative, fmtWhen, h, icon, isoToSastLocal, sastLocalToIso, toast, TZ } from "./dom";
import { editorFor } from "./editors";
import { CHANNEL_LABEL, channelBadge, statusPill, STATUS_LABEL } from "./labels";

type Tab = "edit" | "preview" | "review" | "history";
type Shell = (main: Node, opts: { title: string; toolbar?: Node | null }) => void;

let dirty = false;

/** Navigate, and re-render even when the hash is already the target. */
function goto(hash: string) {
  if (location.hash === hash) window.dispatchEvent(new HashChangeEvent("hashchange"));
  else location.hash = hash;
}
window.addEventListener("beforeunload", (e) => {
  if (dirty) e.preventDefault();
});

function confirmLeave(): boolean {
  if (!dirty) return true;
  if (window.confirm("You have unsaved changes. Leave without saving?")) {
    dirty = false;
    return true;
  }
  return false;
}

/** A preview frame of one version, through a signed link. No script runs inside it. */
async function previewFrame(item: ItemDetail, versionId: string, device: "desktop" | "phone") {
  const link = await api.previewLink(item.id, versionId, 3600);
  const frame = h("iframe", { class: "preview-frame", src: link.path, title: `Preview of ${item.title}`, sandbox: "allow-same-origin", loading: "lazy", referrerpolicy: "no-referrer" });
  return h(
    "div",
    { class: `preview-box device-${device}` },
    frame,
    h("a", { class: "text-link", href: link.path, target: "_blank", rel: "noopener noreferrer" }, icon("external", "ic ic-sm"), "Open full page (link works for an hour, no sign-in needed)"),
  );
}

function nextStep(item: ItemDetail, go: (t: Tab) => void) {
  const v = item.latestVersion;
  const me = session.me!;
  let text: string;
  let action: HTMLElement | null = null;
  switch (item.status) {
    case "draft":
      text = `V${item.latestVersionNumber} is a draft. When it reads right, ask for approval.`;
      if (can("approval:request")) action = h("button", { class: "btn btn-primary", onclick: () => go("review") }, icon("send"), h("span", {}, "Ask for approval"));
      break;
    case "in_review":
      text = `V${item.pendingApproval?.versionNumber} is waiting for approval, asked by ${item.pendingApproval?.requestedByName} ${fmtRelative(item.pendingApproval!.requestedAt)}.`;
      if (isApprover()) action = h("button", { class: "btn btn-primary", onclick: () => go("review") }, icon("checkCircle"), h("span", {}, "Review it"));
      break;
    case "changes_requested":
      text = "An approver asked for changes. Edit and save a new version, then ask again.";
      action = h("button", { class: "btn btn-ghost", onclick: () => go("history") }, "See why");
      break;
    case "approved":
      text = `V${item.latestVersionNumber} is approved. Schedule it or publish it now.`;
      if (can("schedule:write")) action = h("button", { class: "btn btn-primary", onclick: () => go("review") }, icon("clock"), h("span", {}, "Schedule"));
      break;
    case "scheduled":
      text = `Scheduled for ${fmtWhen(item.activeSchedule?.runAt)} (SAST) to ${item.activeSchedule?.target}.`;
      break;
    case "publishing": {
      const ext = item.publications.find((p) => p.status === "external");
      text = ext
        ? `Zernio holds this post for ${fmtWhen(ext.createdAt)} (SAST). The publisher confirms here once LinkedIn shows it.`
        : "Handed to the publisher. It reports back here when the post is live.";
      break;
    }
    case "published": {
      const p = item.publications.find((x) => x.status === "published");
      text = `Live since ${fmtWhen(item.publishedAt)}.`;
      if (p?.permalink) action = h("a", { class: "btn btn-ghost", href: p.permalink, target: "_blank", rel: "noopener noreferrer" }, icon("external"), h("span", {}, "Open the post"));
      else if (item.channel === "blog") action = h("a", { class: "btn btn-ghost", href: `https://strategyedge.co.za/blog/${item.slug}/`, target: "_blank", rel: "noopener noreferrer" }, icon("external"), h("span", {}, "Open on the website"));
      break;
    }
    case "failed":
      text = `Publishing failed: ${item.publications[0]?.error ?? "no reason given"}. Retry from Review.`;
      action = h("button", { class: "btn btn-primary", onclick: () => go("review") }, "Retry");
      break;
    case "archived":
      text = "Archived. Restore it to work on it again.";
      break;
  }
  void me;
  void v;
  return h("div", { class: `next-step st-${item.status}`, role: "status" }, h("p", {}, text), action);
}

export async function renderItem(id: string, tab: Tab | undefined, shell: Shell, current: () => boolean = () => true) {
  dirty = false;
  const item = await api.get(id);
  if (!current()) return;
  const tabNow: Tab = tab ?? (item.status === "in_review" && isApprover() ? "review" : "edit");
  const go = (t: Tab) => {
    if (t !== tabNow && !confirmLeave()) return;
    goto(`#/item/${id}/${t}`);
  };
  const tabs = h(
    "div",
    { class: "tabs", role: "tablist", "aria-label": "Item" },
    ...(
      [
        ["edit", "Edit", "edit"],
        ["preview", "Preview", "eye"],
        ["review", "Approve and publish", "checkCircle"],
        ["history", "History", "history"],
      ] as const
    ).map(([k, label, ic]) => h("button", { role: "tab", class: `tab${k === tabNow ? " on" : ""}`, "aria-selected": k === tabNow ? "true" : "false", onclick: () => go(k), "data-tab": k }, icon(ic, "ic ic-sm"), h("span", {}, label))),
  );
  const head = h(
    "div",
    { class: "item-head" },
    h("a", { class: "back", href: "#/board", onclick: (e: Event) => (confirmLeave() ? undefined : e.preventDefault()) }, icon("arrowLeft", "ic ic-sm"), "Board"),
    h("div", { class: "item-title-row" }, channelBadge(item.channel), statusPill(item.status), h("span", { class: "ver" }, `V${item.latestVersionNumber}`)),
    h("h2", { class: "item-title" }, item.title),
    h("p", { class: "item-sub" }, h("code", {}, item.slug), item.latestVersion ? ` · hash ${item.latestVersion.hash.slice(0, 12)} · saved ${fmtRelative(item.latestVersion.createdAt)} by ${item.latestVersion.createdByName}` : ""),
  );
  const body = h("div", { class: "tab-body", role: "tabpanel" });
  shell(h("div", { class: "item" }, head, nextStep(item, go), tabs, body), { title: CHANNEL_LABEL[item.channel] });

  try {
    if (tabNow === "edit") await editTab(item, body);
    else if (tabNow === "preview") await previewTab(item, body);
    else if (tabNow === "review") await reviewTab(item, body);
    else await historyTab(item, body);
  } catch (e) {
    if (current()) toast(explain(e), "error");
  }
}

/* ------------------------------------------------------------------ edit */

async function editTab(item: ItemDetail, body: HTMLElement) {
  const v = item.latestVersion!;
  const readOnly = !can("content:write") || item.status === "publishing";
  const ed = editorFor(item.channel, v.payload, readOnly);
  const note = h("input", { type: "text", placeholder: "What changed? (optional, shows in history)", maxlength: 500, "aria-label": "Change note" });
  const state = h("span", { class: "save-state" }, readOnly ? (item.status === "publishing" ? "Locked while publishing" : "Read only") : "No changes");
  const save = h("button", { class: "btn btn-primary", disabled: true, "data-testid": "save-version" }, icon("check"), h("span", {}, `Save as V${item.latestVersionNumber + 1}`));
  ed.onChange(() => {
    dirty = true;
    state.textContent = "Unsaved changes";
    state.classList.add("dirty");
    save.removeAttribute("disabled");
  });
  save.addEventListener("click", async () => {
    save.setAttribute("disabled", "");
    try {
      const r = await api.newVersion(item.id, { baseVersionId: v.id, payload: ed.read(), note: note.value.trim() || undefined });
      dirty = false;
      if (r.unchanged) toast("Nothing changed.", "info");
      else toast(`Saved V${r.item.latestVersionNumber}.${r.sanitized ? " Unsafe HTML was removed." : ""}${item.pendingApproval ? " The open approval request was withdrawn: ask again for the new version." : ""}`, "ok");
      if (r.unchanged) save.removeAttribute("disabled");
      else goto(`#/item/${item.id}/edit`);
    } catch (e) {
      toast(explain(e), "error");
      save.removeAttribute("disabled");
    }
  });
  const bar = readOnly ? null : h("div", { class: "save-bar" }, note, state, save);
  const preview = h("div", { class: "side-preview" }, h("p", { class: "fine" }, `Preview of the saved V${v.number}`), await previewFrame(item, v.id, "desktop"));
  body.append(h("div", { class: "edit-grid" }, h("div", { class: "edit-col" }, ed.el, bar), preview));
}

/* ------------------------------------------------------------------ preview */

async function previewTab(item: ItemDetail, body: HTMLElement) {
  const versions = (await api.versions(item.id)).items;
  let device: "desktop" | "phone" = window.innerWidth < 700 ? "phone" : "desktop";
  let vid = item.latestVersionId!;
  const host = h("div", { class: "preview-host" });
  const draw = async () => host.replaceChildren(await previewFrame(item, vid, device));
  const sel = h(
    "select",
    { class: "select", "aria-label": "Version", onchange: (e: Event) => ((vid = (e.target as HTMLSelectElement).value), void draw()) },
    ...versions.map((v) => h("option", { value: v.id, selected: v.id === vid }, `V${v.number}${v.id === item.publishedVersionId ? " (live)" : ""}${v.id === item.approvedVersionId ? " (approved)" : ""} · ${fmtWhen(v.createdAt)}`)),
  );
  const devBtn = (d: "desktop" | "phone", label: string, ic: string) =>
    h("button", { class: `chip${device === d ? " on" : ""}`, "aria-pressed": device === d ? "true" : "false", onclick: (e: Event) => {
      device = d;
      for (const b of (e.currentTarget as HTMLElement).parentElement!.querySelectorAll("button")) b.classList.toggle("on", b === e.currentTarget);
      void draw();
    } }, icon(ic, "ic ic-sm"), label);
  body.append(h("div", { class: "preview-tools" }, sel, h("div", { class: "chips" }, devBtn("desktop", "Desktop", "monitor"), devBtn("phone", "Phone", "phone"))), host);
  await draw();
}

/* ------------------------------------------------------------------ review: approve, schedule, publish */

function card(title: string, ...children: (Node | null)[]) {
  return h("section", { class: "panel" }, h("h3", {}, title), ...children);
}

async function reviewTab(item: ItemDetail, body: HTMLElement) {
  const me = session.me!;
  const v = item.latestVersion!;
  const reload = () => goto(`#/item/${item.id}/review`);
  const act = async (btn: HTMLButtonElement, fn: () => Promise<unknown>, ok: string) => {
    btn.setAttribute("disabled", "");
    try {
      await fn();
      toast(ok, "ok");
      reload();
    } catch (e) {
      toast(explain(e), "error");
      btn.removeAttribute("disabled");
    }
  };

  /* approval */
  let approval: HTMLElement;
  const ap = item.pendingApproval;
  if (ap) {
    const reason = h("textarea", { rows: 3, placeholder: "Reason (needed to deny, optional to approve)", "aria-label": "Reason" });
    const own = v.createdBy === me.id;
    const approve = h("button", { class: "btn btn-approve", disabled: !isApprover() || own, "data-testid": "approve" }, icon("check"), h("span", {}, `Approve V${ap.versionNumber}`));
    const deny = h("button", { class: "btn btn-deny", disabled: !isApprover(), "data-testid": "deny" }, icon("x"), h("span", {}, "Deny"));
    approve.addEventListener("click", () => act(approve, () => api.decide(ap.id, { decision: "approve", versionHash: ap.versionHash, reason: reason.value.trim() || undefined }), `Approved V${ap.versionNumber}.`));
    deny.addEventListener("click", () => {
      if (!reason.value.trim()) {
        reason.focus();
        toast("Say what should change, so the writer knows.", "info");
        return;
      }
      void act(deny, () => api.decide(ap.id, { decision: "deny", versionHash: ap.versionHash, reason: reason.value.trim() }), "Denied. The writer has your reason.");
    });
    approval = card(
      "Approval",
      h("p", {}, `V${ap.versionNumber} asked by ${ap.requestedByName}, ${fmtRelative(ap.requestedAt)}.`),
      h("p", { class: "hash" }, "Approving binds to exactly this content: ", h("code", {}, ap.versionHash)),
      ap.onApprove
        ? h("p", { class: "fine warn", "data-testid": "on-approve" }, ap.onApprove.runAt ? `Approving also schedules it for ${fmtWhen(ap.onApprove.runAt)} (${TZ}).` : `Approving also publishes it straight away${item.channel === "linkedin" ? " to LinkedIn" : ""}.`)
        : null,
      !isApprover() ? h("p", { class: "fine" }, "Only approvers can decide. You can still read and comment.") : own ? h("p", { class: "fine warn" }, `You wrote V${ap.versionNumber}, so someone else must approve it.`) : null,
      isApprover() ? reason : null,
      isApprover() ? h("div", { class: "btn-row" }, deny, approve) : null,
    );
  } else if (item.status === "draft" || item.status === "changes_requested") {
    const msg = h("input", { type: "text", placeholder: "Note for the approver (optional)", "aria-label": "Note for the approver" });
    const ask = h("button", { class: "btn btn-primary", disabled: !can("approval:request"), "data-testid": "request-approval" }, icon("send"), h("span", {}, `Ask for approval of V${v.number}`));
    ask.addEventListener("click", () => act(ask, () => api.requestApproval(item.id, { versionId: v.id, versionHash: v.hash, message: msg.value.trim() || undefined }), "Approval requested."));
    approval = card("Approval", h("p", {}, `V${v.number} has not been put up for approval.`), msg, h("div", { class: "btn-row" }, ask));
  } else {
    const last = item.approvedVersionId ? `V${item.latestVersionNumber} is approved.` : STATUS_LABEL[item.status];
    approval = card("Approval", h("p", {}, last));
  }

  /* schedule and publish */
  let publishing: HTMLElement;
  const canPublish = can("schedule:write");
  if (["approved", "failed", "scheduled"].includes(item.status) && item.approvedVersionId === v.id) {
    const nextMorning = (() => {
      const d = new Date(Date.now() + 2 * 3600_000);
      d.setUTCDate(d.getUTCDate() + 1);
      while ([0, 6].includes(d.getUTCDay())) d.setUTCDate(d.getUTCDate() + 1);
      return `${d.toISOString().slice(0, 10)}T08:00`;
    })();
    const when = h("input", { type: "datetime-local", value: item.activeSchedule ? isoToSastLocal(item.activeSchedule.runAt) : nextMorning, "aria-label": "Publish at (SAST)" });
    const sched = h("button", { class: "btn btn-ghost", disabled: !canPublish, "data-testid": "schedule" }, icon("clock"), h("span", {}, item.activeSchedule ? "Move schedule" : "Schedule"));
    sched.addEventListener("click", () => act(sched, () => api.schedule(item.id, { versionId: v.id, versionHash: v.hash, runAt: sastLocalToIso(when.value) }), `Scheduled for ${fmtWhen(sastLocalToIso(when.value))}.`));
    const now = h("button", { class: "btn btn-primary", disabled: !canPublish, "data-testid": "publish-now" }, icon("send"), h("span", {}, item.status === "failed" ? "Retry now" : "Publish now"));
    now.addEventListener("click", () => {
      if (!window.confirm(item.channel === "blog" ? `Publish V${v.number} to the website now?` : `Send V${v.number} to ${item.channel} now?`)) return;
      void act(now, () => api.publish(item.id, { versionId: v.id, versionHash: v.hash }), item.channel === "blog" ? "Published. The website shows it on its next build." : "Sent to the publisher.");
    });
    const cancel = item.activeSchedule ? h("button", { class: "btn btn-ghost", "data-testid": "cancel-schedule" }, "Cancel schedule") : null;
    cancel?.addEventListener("click", () => act(cancel, () => api.cancelSchedule(item.activeSchedule!.id), "Schedule cancelled."));
    publishing = card(
      "Schedule or publish",
      item.activeSchedule ? h("p", {}, `Scheduled for ${fmtWhen(item.activeSchedule.runAt)} (${TZ}).`) : h("p", {}, `V${v.number} is approved and ready.`),
      h("label", { class: "field" }, h("span", { class: "field-label" }, "Publish at (South African time)"), when),
      !canPublish ? h("p", { class: "fine" }, "Only approvers and admins can schedule or publish.") : null,
      h("div", { class: "btn-row" }, cancel, sched, now),
    );
  } else {
    publishing = card("Schedule or publish", h("p", { class: "fine" }, item.status === "published" ? "This version is live." : item.status === "publishing" ? "The publisher is working on it." : "Approve the latest version first."));
  }

  const pubs = item.publications.length
    ? card(
        "Publications",
        h(
          "ul",
          { class: "plain" },
          ...item.publications.map((p) => h("li", {}, h("b", {}, p.status), ` · ${p.target} · ${fmtWhen(p.publishedAt ?? p.createdAt)}`, p.permalink ? h("a", { class: "text-link", href: p.permalink, target: "_blank", rel: "noopener noreferrer" }, " open") : null, p.error ? h("span", { class: "warn" }, ` · ${p.error}`) : null)),
        ),
      )
    : null;
  const refs = item.externalRefs.length ? card("Linked records", h("ul", { class: "plain mono" }, ...item.externalRefs.map((r) => h("li", {}, `${r.system}.${r.key} = ${r.value}`)))) : null;

  const archive =
    can("content:write") && item.status !== "publishing"
      ? h(
          "button",
          {
            class: "btn btn-quiet",
            onclick: (e: Event) =>
              act(e.currentTarget as HTMLButtonElement, () => api.update(item.id, { revision: item.revision, archived: item.status !== "archived" }), item.status === "archived" ? "Restored as a draft." : "Archived."),
          },
          item.status === "archived" ? "Restore" : "Archive",
        )
      : null;
  body.append(h("div", { class: "review-grid" }, approval, publishing, pubs, refs, archive ? h("div", { class: "danger-zone" }, archive) : null));
}

/* ------------------------------------------------------------------ history */

async function historyTab(item: ItemDetail, body: HTMLElement) {
  const [hist, versions, comments] = await Promise.all([api.history(item.id), api.versions(item.id), api.comments(item.id)]);
  const timeline = h(
    "ol",
    { class: "timeline" },
    ...hist.items.map((e) =>
      h(
        "li",
        { class: `tl tl-${e.kind}` },
        h("span", { class: "tl-dot", "aria-hidden": "true" }),
        h("div", {}, h("b", {}, e.title), h("small", {}, `${e.by}${e.via ? ` · via ${e.via}` : ""} · ${fmtWhen(e.at)}`), e.detail ? h("p", { class: "tl-detail" }, e.detail) : null),
      ),
    ),
  );
  const vlist = h(
    "ul",
    { class: "versions" },
    ...versions.items.map((v: Version) =>
      h(
        "li",
        {},
        h("b", {}, `V${v.number}`),
        h("span", {}, `${v.createdByName} · ${fmtWhen(v.createdAt)}${v.note ? ` · ${v.note}` : ""}`),
        h("code", { title: v.hash }, v.hash.slice(0, 10)),
        h("button", { class: "btn btn-quiet", onclick: async () => window.open((await api.previewLink(item.id, v.id, 3600)).path, "_blank", "noopener") }, icon("eye", "ic ic-sm"), "Preview"),
      ),
    ),
  );
  const text = h("textarea", { rows: 2, placeholder: "Add a comment", "aria-label": "Comment" });
  const add = h("button", { class: "btn btn-ghost", disabled: !can("content:write") }, "Comment");
  add.addEventListener("click", async () => {
    if (!text.value.trim()) return;
    try {
      await api.comment(item.id, { body: text.value.trim() });
      toast("Comment added.", "ok");
      goto(`#/item/${item.id}/history`);
    } catch (e) {
      toast(explain(e), "error");
    }
  });
  body.append(
    h(
      "div",
      { class: "history-grid" },
      h("section", { class: "panel" }, h("h3", {}, "Timeline"), timeline),
      h(
        "div",
        {},
        h("section", { class: "panel" }, h("h3", {}, `Versions (${versions.items.length})`), vlist),
        h("section", { class: "panel" }, h("h3", {}, `Comments (${comments.items.length})`), ...comments.items.map((c) => h("div", { class: "comment" }, h("b", {}, c.byName), h("small", {}, ` ${fmtWhen(c.createdAt)}${c.via ? ` · ${c.via}` : ""}`), h("p", {}, c.body))), text, h("div", { class: "btn-row" }, add)),
      ),
    ),
  );
}
